import { aspectValue, type AspectRatio } from '@panels/shared';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  SNAP_HOLD_POINTER_TYPES,
  SNAP_MIN_LENGTH_PX,
  SNAP_STILL_MS,
  SNAP_STILL_RADIUS_PX,
  type DrawingSession,
} from '../lib/drawing.js';
import { clearCanvas, prepareCanvas, renderOpLog } from '../lib/render.js';

export interface DrawCanvasHandle {
  repaint: () => void;
  resetView: () => void;
}

interface Props {
  session: DrawingSession;
  aspectRatio: AspectRatio;
  /** Fired when the op log changed (stroke finished, or abandoned). */
  onChange: () => void;
  /** Fired continuously while a stroke is in progress, for throttled draft saves. */
  onDrawing?: () => void;
  /** The stroke in flight was just straightened (Shift, or holding still). */
  onSnap?: () => void;
  /** Any pointer input on the canvas — the activity clock (Round 2 §1.1). */
  onInput?: () => void;
  /** A stroke was refused because the panel is full (Round 2.2 §3). */
  onBlocked?: () => void;
  disabled?: boolean;
}

interface View {
  scale: number;
  tx: number;
  ty: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;

/**
 * The drawing surface. Pointer Events throughout, so mouse, touch and stylus all
 * take the same path, and `touch-action: none` keeps the page from scrolling out
 * from under a stroke.
 *
 * Two fingers pan and zoom for detail work; the first finger's accidental mark is
 * discarded the moment a second one lands.
 *
 * Straight lines (Round 2 §2.5): hold Shift while drawing, or on a touch screen
 * keep the finger still at the end of a stroke. Either way the stroke snaps to a
 * line, and moving on afterwards aims its far end.
 */
export const DrawCanvas = forwardRef<DrawCanvasHandle, Props>(function DrawCanvas(
  { session, aspectRatio, onChange, onDrawing, onSnap, onInput, onBlocked, disabled = false },
  ref,
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef({ width: 0, height: 0 });
  const viewRef = useRef<View>({ scale: 1, tx: 0, ty: 0 });
  const [zoomed, setZoomed] = useState(false);

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drawingPointer = useRef<number | null>(null);
  const pinch = useRef<{ dist: number; midX: number; midY: number; view: View } | null>(null);

  /* Hold-to-snap: where the pointer went down, and where it last settled. */
  const strokeStart = useRef<{ x: number; y: number } | null>(null);
  const stillAnchor = useRef<{ x: number; y: number } | null>(null);
  const stillTimer = useRef<number | null>(null);
  const onSnapRef = useRef(onSnap);
  onSnapRef.current = onSnap;

  const repaint = () => {
    const canvas = canvasRef.current;
    const { width, height } = sizeRef.current;
    if (!canvas || width === 0) return;
    const ctx = prepareCanvas(canvas, width, height);
    if (!ctx) return;

    const { scale, tx, ty } = viewRef.current;
    clearCanvas(ctx, width, height, '#ffffff');
    ctx.save();
    ctx.translate(tx, ty);
    ctx.scale(scale, scale);
    // Already cleared above — clearing again under the transform would punch a hole.
    renderOpLog(ctx, session.toOpLog(), width, height, { clear: false });
    ctx.restore();
  };

  const resetView = () => {
    viewRef.current = { scale: 1, tx: 0, ty: 0 };
    setZoomed(false);
    repaint();
  };

  useImperativeHandle(ref, () => ({ repaint, resetView }));

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const measure = () => {
      const width = wrap.clientWidth;
      const height = width / aspectValue(aspectRatio);
      if (width === sizeRef.current.width && height === sizeRef.current.height) return;
      sizeRef.current = { width, height };
      repaint();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectRatio]);

  useEffect(() => {
    repaint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  /* ------------------------------------------------------- coordinates */

  const localPoint = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  /** Canvas pixels -> normalized 0..1, undoing the pan/zoom transform. */
  const toNormalized = (x: number, y: number) => {
    const { width, height } = sizeRef.current;
    const { scale, tx, ty } = viewRef.current;
    return { nx: (x - tx) / scale / width, ny: (y - ty) / scale / height };
  };

  const clampView = (view: View): View => {
    const { width, height } = sizeRef.current;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, view.scale));
    // Keep the panel covering the viewport: never pan past its edges.
    const tx = Math.min(0, Math.max(width - width * scale, view.tx));
    const ty = Math.min(0, Math.max(height - height * scale, view.ty));
    return { scale, tx, ty };
  };

  /* ---------------------------------------------------- straight lines */

  const clearStill = () => {
    if (stillTimer.current !== null) clearTimeout(stillTimer.current);
    stillTimer.current = null;
  };

  const snapNow = () => {
    if (session.snapStroke()) {
      clearStill();
      repaint();
      onSnapRef.current?.();
    }
  };

  /** The finger settled here: straighten if it stays put for SNAP_STILL_MS. */
  const armStill = (point: { x: number; y: number }) => {
    clearStill();
    stillAnchor.current = point;
    stillTimer.current = window.setTimeout(() => {
      stillTimer.current = null;
      const start = strokeStart.current;
      const at = stillAnchor.current;
      if (!session.isDrawing || session.isSnapped || !start || !at) return;
      // A dot or a tiny tick is not a line anyone meant to straighten.
      if (Math.hypot(at.x - start.x, at.y - start.y) < SNAP_MIN_LENGTH_PX) return;
      snapNow();
    }, SNAP_STILL_MS);
  };

  // Pressing Shift mid-stroke straightens it from there, without waiting for the mouse to move.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Shift' && session.isDrawing) snapNow();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearStill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  /* ------------------------------------------------------------ events */

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const point = localPoint(event);
    pointers.current.set(event.pointerId, point);
    event.currentTarget.setPointerCapture(event.pointerId);
    onInput?.();

    if (pointers.current.size === 1) {
      const { nx, ny } = toNormalized(point.x, point.y);
      // Shift held as the stroke starts: straight from the first point.
      const began = session.beginStroke(nx, ny, event.pointerType === 'pen' ? event.pressure : undefined, {
        straight: event.shiftKey,
      });
      if (!began) {
        // The panel is full: no new marks, but undo and Done still work.
        onBlocked?.();
        return;
      }
      drawingPointer.current = event.pointerId;
      strokeStart.current = point;
      if (event.shiftKey) onSnapRef.current?.();
      else if (SNAP_HOLD_POINTER_TYPES.includes(event.pointerType)) armStill(point);
      repaint();
      return;
    }

    if (pointers.current.size === 2) {
      // That first touch was the start of a pinch, not a mark.
      clearStill();
      if (session.isDrawing) {
        session.abortStroke();
        drawingPointer.current = null;
        repaint();
      }
      const [a, b] = [...pointers.current.values()];
      pinch.current = {
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2,
        view: { ...viewRef.current },
      };
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    if (!pointers.current.has(event.pointerId)) return;
    const point = localPoint(event);
    pointers.current.set(event.pointerId, point);

    if (pinch.current && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      const start = pinch.current;

      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, (start.view.scale * dist) / start.dist));
      // Keep whatever was under the original midpoint under the new one.
      const worldX = (start.midX - start.view.tx) / start.view.scale;
      const worldY = (start.midY - start.view.ty) / start.view.scale;
      viewRef.current = clampView({ scale, tx: midX - worldX * scale, ty: midY - worldY * scale });
      setZoomed(viewRef.current.scale > 1.01);
      repaint();
      return;
    }

    if (drawingPointer.current === event.pointerId && session.isDrawing) {
      onInput?.();
      if (event.shiftKey && !session.isSnapped) snapNow();
      // Moving off the spot where it settled restarts the stillness clock.
      if (
        !session.isSnapped &&
        SNAP_HOLD_POINTER_TYPES.includes(event.pointerType) &&
        stillAnchor.current &&
        Math.hypot(point.x - stillAnchor.current.x, point.y - stillAnchor.current.y) > SNAP_STILL_RADIUS_PX
      ) {
        armStill(point);
      }
      // Coalesced events give us the full-rate samples the OS captured.
      const events = typeof event.nativeEvent.getCoalescedEvents === 'function'
        ? event.nativeEvent.getCoalescedEvents()
        : [];
      const rect = event.currentTarget.getBoundingClientRect();
      if (events.length > 0) {
        for (const e of events) {
          const { nx, ny } = toNormalized(e.clientX - rect.left, e.clientY - rect.top);
          session.extendStroke(nx, ny, event.pointerType === 'pen' ? e.pressure : undefined);
        }
      } else {
        const { nx, ny } = toNormalized(point.x, point.y);
        session.extendStroke(nx, ny, event.pointerType === 'pen' ? event.pressure : undefined);
      }
      repaint();
      onDrawing?.();
    }
  };

  const endPointer = (event: React.PointerEvent<HTMLCanvasElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;

    if (drawingPointer.current === event.pointerId) {
      drawingPointer.current = null;
      clearStill();
      strokeStart.current = null;
      stillAnchor.current = null;
      if (session.isDrawing) {
        session.endStroke();
        repaint();
        onChange();
      }
    }
  };

  return (
    <div ref={wrapRef} className="draw-canvas" style={{ aspectRatio: aspectRatio.replace(':', ' / ') }}>
      <canvas
        ref={canvasRef}
        // Stops the page scrolling or zooming while a stroke is in progress.
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={endPointer}
      />
      {zoomed ? (
        <button type="button" className="zoom-reset" onClick={resetView}>
          Reset zoom
        </button>
      ) : null}
    </div>
  );
});
